import React, { ChangeEvent } from 'react';

import {
  VenueTemplate,
  VenueLocationLong,
  VenueLocationLat,
} from '../../shared/types/venueTemplate';
import {
  FormGroup,
  Input,
  Button,
  Header,
  Checkbox,
  RadioButton,
  RadioButtonGroup,
  InlineIcon,
  TextButton,
  makeItem,
  TypeaheadItem,
  TextInput,
} from '@spark-labs/avocado-ui';

import { BrandVenueType } from '../../shared/types/brand';
import { InputElement } from '@spark-labs/avocado-ui/dist/components/Input';
import { PropertyType } from '@go-shared/types/property';
import { getPropertyConfiguration } from '@go-shared/lib/propertyConfig';
import { UploadButton } from './UIkit/forms/UploadButton/UploadButton';
import {
  bare,
  update as updateVenueTemplate,
  uploadVenueAttributesFile,
  uploadVenueImage,
} from '@go-client/lib/api/venueTemplate';
import { User } from '@go-shared/types/user';
import { makeFullName } from '@go-shared/lib/utils';
import { BubbleButton, BubbleFormGroup, BubbleImageSelector } from './BubbleUI';
import { ObjectID } from 'bson';
import { TemplateFormImages } from './TemplateFormImages';
import { toast } from './UIkit/Toast/Toast';
import VenueForm from './VenueForm';
import { VenueDisposition, VenueHours } from '@go-shared/types/plan';
import { DEFAULT_DAY_START } from '@go-shared/lib/property';
import { ServerInfo } from '@go-shared/types/info';
import McmVisibilityTable from './McmVisibilityTable';
import AudienceVisibilityTable from './AudienceVisibilityTable';
import { CallToActions } from '@go-client/components/BasicForms/CallToActions';
import { DailyProgramCta } from '@go-shared/types/dailyProgram';

export interface VenueTemplateFormProps {
  user: User;
  users: User[];
  serverInfo: ServerInfo;
  venueTemplate: VenueTemplate;
  propertyType: PropertyType;
  showNewVenueBtn: boolean;
  onSubmit?: (venueTemplate: VenueTemplate, files?: File[]) => void;
  onArchive?: () => void;
  refresh?: () => void;
  onEditedVenueChange: () => void;
}

export interface VenueTemplateFormState {
  editedVenueTemplate: VenueTemplate;
  groupAdminTypeaheadValue?: string;
  userOptions: TypeaheadItem[];
  // This map is used for performance reasons. When a checkbox changes, the
  // corresponding _id is looked up in the map and updated. When the form is
  // submitted, this map is applied to the venue template
  venueTypeMap: { [id: string]: boolean };
  uploadQueue: File[];
}

export default class VenueTemplateForm extends React.Component<
  VenueTemplateFormProps,
  VenueTemplateFormState
> {
  public readonly state: VenueTemplateFormState = {
    editedVenueTemplate: this.props.venueTemplate,
    groupAdminTypeaheadValue: undefined,
    userOptions: [],
    venueTypeMap: {},
    uploadQueue: [],
  };

  constructor(props: VenueTemplateFormProps) {
    super(props);

    let { venueTemplate } = props;

    venueTemplate.property.brand.venueTypes.forEach(vt => {
      this.state.venueTypeMap[vt._id] = venueTemplate.types.findIndex(t => t._id === vt._id) !== -1;
    });

    this.state.userOptions = this.props.users.map(u =>
      makeItem(u._id, makeFullName(u.firstName, u.lastName))
    );
  }

  public render() {
    let venueTemplate = this.state.editedVenueTemplate;
    let levelLabel = getPropertyConfiguration(this.props.propertyType, 'levelLabel');
    let displayLocation = getPropertyConfiguration(this.props.propertyType, 'showLocation');

    let { property } = this.props.venueTemplate;

    const fakeVenueData: VenueDisposition = {
      _id: new ObjectID().toHexString(),
      template: venueTemplate._id,
      hours: venueTemplate.bookingHours,
      mealPeriods: [],
      callToActions: { syncWithTemplate: true, values: [] },
    };

    return (
      <div className="border-t border-grey pt-2 p-4 mt-6" style={{ width: '500px' }}>
        <fieldset disabled={this.isLocked()}>
          <div className="flex">
            <div className="w-full p-4">
              <BubbleFormGroup label="Images" className="mb-3">
                <BubbleImageSelector
                  maxItems={3}
                  value={venueTemplate.images}
                  onValue={images =>
                    this.setState({
                      editedVenueTemplate: {
                        ...this.state.editedVenueTemplate,
                        images,
                      },
                    })
                  }
                />
              </BubbleFormGroup>
              <FormGroup>
                <Input
                  name="name"
                  label="Name"
                  onChange={this.handleInputChange}
                  value={venueTemplate.name}
                />
              </FormGroup>
              <FormGroup>
                <TextInput
                  name="description"
                  label="Description"
                  onChange={this.handleInputChange}
                  value={venueTemplate.description}
                  rows={5}
                />
              </FormGroup>
              <FormGroup>
                <div className="flex flex-wrap space-x-4">
                  <div className="flex-grow">
                    <Input
                      name="level"
                      label={levelLabel}
                      onChange={this.handleInputChange}
                      value={venueTemplate.level}
                    />
                  </div>
                  <div className="flex-grow">
                    <Input
                      name="occupancy"
                      label="Occupancy"
                      onChange={this.handleInputChange}
                      value={venueTemplate.occupancy}
                    />
                  </div>
                </div>
              </FormGroup>
              <FormGroup className="mt-6 mb-6 flex flex-col items-baseline">
                <div className="header flex flex-row items-center justify-between w-full">
                  <Header size="sm">Venue Attributes</Header>
                  <div className="flex items-baseline mb-2 ml-8">
                    <UploadButton
                      accept=".pdf"
                      singleUpload
                      onFilesChange={async files => this.uploadAttributesFile(files[0])}
                      // Render props FTW
                      render={upload => {
                        return <TextButton onClick={upload}>Upload File</TextButton>;
                      }}
                    />
                  </div>
                </div>

                <div className="flex flex-col w-full">
                  <div className="text-grey-dark" style={{ minWidth: 50 }}>
                    {this.renderPDFFileNames()}
                  </div>
                </div>
              </FormGroup>
              <FormGroup>
                <Header size="sm">Configurations</Header>
                <div className="-mb-5">
                  {venueTemplate.configurations.map(conf => (
                    <div key={conf._id} className="mb-5">
                      <TemplateFormImages
                        onImageRemoved={idx => this.handleConfigurationImageRemove(conf._id, idx)}
                        onImageUploaded={file =>
                          this.handleConfigurationImageUpload(conf._id, file)
                        }
                        template={conf}
                      />
                      <div className="flex items-end space-x-4">
                        <div className="flex-grow">
                          <Input
                            name="name"
                            label="Name"
                            onChange={evt => this.handleConfigurationInputChange(conf._id, evt)}
                            value={conf.name}
                          />
                        </div>
                        <div className="flex-grow">
                          <Input
                            name="occupancy"
                            label="Occupancy"
                            onChange={evt => this.handleConfigurationInputChange(conf._id, evt)}
                            value={conf.occupancy}
                          />
                        </div>
                        <BubbleButton
                          variant="crimson"
                          className="p-1-i"
                          onClick={() => this.deleteVenueConfiguration(conf._id)}
                          inverse
                        >
                          <InlineIcon name="close" />
                        </BubbleButton>
                      </div>
                    </div>
                  ))}
                  <BubbleButton
                    variant="lime"
                    onClick={this.addVenueConfiguration}
                    className="mb-5"
                  >
                    <div className="flex items-center">
                      <InlineIcon name="plus" className="p-0-i mr-1" />
                      <span>Add Venue Configuration</span>
                    </div>
                  </BubbleButton>
                </div>
              </FormGroup>
              <FormGroup>
                <Header size="sm">Types</Header>
                <div className="flex flex-wrap">
                  {venueTemplate.property.brand.venueTypes.map((vt, i) => (
                    <span key={i} className="w-1/2 my-1">
                      <Checkbox
                        onChange={this.handleVenueTypeChange}
                        value={vt._id}
                        label={vt.name}
                        checked={this.state.venueTypeMap[vt._id]}
                      />
                    </span>
                  ))}
                </div>
              </FormGroup>
              {displayLocation && (
                <FormGroup>
                  <Header size="sm">Location</Header>
                  <div className="flex w-full">
                    <div className="flex flex-col w-1/2">
                      <RadioButtonGroup
                        name="locationLong"
                        onChange={this.handleLocationLongChange}
                        groupValue={venueTemplate.locationLong ? venueTemplate.locationLong : ' '}
                      >
                        {groupProps => (
                          <>
                            <RadioButton value={' '} label="Unassigned" {...groupProps} />
                            <RadioButton value={'fwd'} label="Fwd" {...groupProps} />
                            <RadioButton value={'mid'} label="Mid" {...groupProps} />
                            <RadioButton value={'aft'} label="Aft" {...groupProps} />
                          </>
                        )}
                      </RadioButtonGroup>
                    </div>
                    <div className="flex flex-col w-1/2">
                      <RadioButtonGroup
                        name="locationLat"
                        onChange={this.handleLocationLatChange}
                        groupValue={venueTemplate.locationLat ? venueTemplate.locationLat : ' '}
                      >
                        {groupProps => (
                          <>
                            <RadioButton value={' '} label="Unassigned" {...groupProps} />
                            <RadioButton value={'prt'} label="Prt" {...groupProps} />
                            <RadioButton value={'stb'} label="Stb" {...groupProps} />
                          </>
                        )}
                      </RadioButtonGroup>
                    </div>
                  </div>
                </FormGroup>
              )}
              <div className="-mx-4">
                <VenueForm
                  onlyVenueHours
                  onlyOpen
                  hideSubmitButton
                  hoursTitle="Booking Hours"
                  template={venueTemplate}
                  mealPeriods={property.brand.mealPeriods}
                  venueData={fakeVenueData}
                  propertyType={this.props.propertyType}
                  hourFormat={property.brand.hourFormat}
                  dayStart={property.dayStart || DEFAULT_DAY_START}
                  onVenueHoursUpdate={this.handleOnUpdateVenueHours}
                  onSubmit={this.handleBookingHoursSubmit}
                />
              </div>
              <FormGroup>
                <div>
                  <Header size="sm">Display Options </Header>
                  <FormGroup className="bg-grey-lighter p-4">
                    <div className="w-full mt-1">
                      <Checkbox
                        onChange={this.handlePrivateChange}
                        label="Not Bookable for Groups"
                        checked={!!venueTemplate.isPrivate}
                      />
                    </div>
                    <div className="w-full mt-3">
                      <Checkbox
                        onChange={this.handleHiddenChange}
                        label="Private (hide from endpoints)"
                        checked={!!venueTemplate.isHidden}
                      />
                    </div>
                  </FormGroup>
                </div>
              </FormGroup>

              <BubbleFormGroup className="my-5">
                <Header size="sm">Call to Actions</Header>
                <CallToActions
                  callToActions={this.state.editedVenueTemplate.callToActions}
                  onUpdate={this.handleCallToActionChange}
                />
              </BubbleFormGroup>

              <FormGroup>
                <Header size="sm">Endpoints</Header>
                <McmVisibilityTable
                  isTemplate
                  sourceMcm={this.state.editedVenueTemplate.mcm}
                  parentMcm={undefined}
                  onUpdateMcm={newMcm =>
                    this.setState({
                      editedVenueTemplate: { ...this.state.editedVenueTemplate, mcm: newMcm },
                    })
                  }
                />
              </FormGroup>
              <FormGroup>
                <Header size="sm">Audience</Header>
                <AudienceVisibilityTable
                  isTemplate
                  sourceAud={this.state.editedVenueTemplate.audience}
                  parentAud={undefined}
                  onUpdateAudience={newAud =>
                    this.setState({
                      editedVenueTemplate: { ...this.state.editedVenueTemplate, audience: newAud },
                    })
                  }
                />
              </FormGroup>
            </div>
          </div>
          {!this.isLocked() && (
            <div className="flex justify-end">
              <div className="mx-2">
                <Button color="red" onClick={this.props.onArchive}>
                  {venueTemplate?.archived ? 'Unarchive' : 'Archive'}
                </Button>
              </div>
              <div className="mx-2">
                <Button color="green" onClick={this.handleFormSubmit}>
                  Submit
                </Button>
              </div>
            </div>
          )}
        </fieldset>
      </div>
    );
  }

  private isLocked = () => !this.props.showNewVenueBtn;

  private handleFormSubmit = async () => {
    if (this.isLocked()) {
      await toast.warning('Venue template editing is locked');
      return;
    }
    // Transfer the map information into the venueTemplate
    let types: BrandVenueType[] = [];

    this.props.venueTemplate.property.brand.venueTypes.forEach(vt => {
      if (this.state.venueTypeMap[vt._id]) types.push(Object.assign({}, vt));
    });

    if (this.props.onSubmit) {
      this.props.onSubmit({ ...this.state.editedVenueTemplate, types }, this.state.uploadQueue);
    }
  };

  private addVenueConfiguration = () => {
    const newConfigurations = [...this.state.editedVenueTemplate.configurations];

    newConfigurations.push({
      _id: new ObjectID().toHexString(),
      name: '',
      occupancy: 0,
      images: [],
    });

    this.setState({
      editedVenueTemplate: {
        ...this.state.editedVenueTemplate,
        configurations: newConfigurations,
      },
    });
  };

  private deleteVenueConfiguration = (idToDelete: string) => {
    const newConfigurations = [...this.state.editedVenueTemplate.configurations].filter(
      c => c._id !== idToDelete
    );

    this.setState({
      editedVenueTemplate: {
        ...this.state.editedVenueTemplate,
        configurations: newConfigurations,
      },
    });
  };

  private handleInputChange = (venue: ChangeEvent<InputElement>) => {
    let newState = { ...this.state.editedVenueTemplate, [venue.target.name]: venue.target.value };
    this.setState({ editedVenueTemplate: newState as any });
    this.updateEditedVenue();
  };

  private handleOnUpdateVenueHours = (venueHours: VenueHours[]) => {
    const newState = { ...this.state.editedVenueTemplate, bookingHours: venueHours };
    this.setState({ editedVenueTemplate: newState as any });
    this.updateEditedVenue();
  };

  private handleConfigurationInputChange = (confId: string, confEvt: ChangeEvent<InputElement>) => {
    const confIdx = this.state.editedVenueTemplate.configurations.findIndex(c => c._id === confId);

    if (confIdx > -1) {
      const activeConf = this.state.editedVenueTemplate.configurations[confIdx];
      const newConf = { ...activeConf, [confEvt.target.name]: confEvt.target.value };

      const newConfigurations = [...this.state.editedVenueTemplate.configurations];
      newConfigurations.splice(confIdx, 1, newConf);

      this.setState({
        editedVenueTemplate: {
          ...this.state.editedVenueTemplate,
          configurations: newConfigurations,
        },
      });
    }
    this.updateEditedVenue();
  };

  private handleConfigurationImageUpload = async (confId: string, file: File) => {
    const confIdx = this.state.editedVenueTemplate.configurations.findIndex(c => c._id === confId);

    if (confIdx > -1) {
      const activeConf = this.state.editedVenueTemplate.configurations[confIdx];
      const result = await uploadVenueImage(this.state.editedVenueTemplate, file);

      if (result.location) {
        const newImages = [...activeConf.images];
        newImages.push(result.location);

        const newConf = { ...activeConf, images: newImages };
        const newConfigurations = [...this.state.editedVenueTemplate.configurations];
        newConfigurations.splice(confIdx, 1, newConf);

        this.setState({
          editedVenueTemplate: {
            ...this.state.editedVenueTemplate,
            configurations: newConfigurations,
          },
        });
      }
    }
    this.updateEditedVenue();
  };

  private handleConfigurationImageRemove = (confId: string, index: number) => {
    const confIdx = this.state.editedVenueTemplate.configurations.findIndex(c => c._id === confId);

    if (confIdx > -1) {
      const activeConf = this.state.editedVenueTemplate.configurations[confIdx];
      const newImages = [...activeConf.images];
      newImages.splice(index, 1);

      const newConf = { ...activeConf, images: newImages };
      const newConfigurations = [...this.state.editedVenueTemplate.configurations];
      newConfigurations.splice(confIdx, 1, newConf);

      this.setState({
        editedVenueTemplate: {
          ...this.state.editedVenueTemplate,
          configurations: newConfigurations,
        },
      });
    }
    this.updateEditedVenue();
  };

  private handleVenueTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    // find the _id in the types map and update it depending on whether it was
    // checked or unchecked.
    this.setState({
      venueTypeMap: { ...this.state.venueTypeMap, [event.target.value]: event.target.checked },
    });
    this.updateEditedVenue();
  };

  private handlePrivateChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      editedVenueTemplate: { ...this.state.editedVenueTemplate, isPrivate: event.target.checked },
    });
    this.updateEditedVenue();
  };

  private handleHiddenChange = (event: ChangeEvent<HTMLInputElement>) => {
    this.setState({
      editedVenueTemplate: { ...this.state.editedVenueTemplate, isHidden: event.target.checked },
    });
    this.updateEditedVenue();
  };

  private handleLocationLongChange = (locationValue: string) => {
    let locationLong: VenueLocationLong | undefined;

    if (locationValue === ' ') {
      locationLong = undefined;
    } else {
      locationLong = locationValue as any;
    }

    this.setState({ editedVenueTemplate: { ...this.state.editedVenueTemplate, locationLong } });
    this.updateEditedVenue();
  };

  private handleLocationLatChange = (locationValue: string) => {
    let locationLat: VenueLocationLat | undefined;

    if (locationValue === ' ') {
      locationLat = undefined;
    } else {
      locationLat = locationValue as any;
    }

    this.setState({ editedVenueTemplate: { ...this.state.editedVenueTemplate, locationLat } });
    this.updateEditedVenue();
  };

  private handleCallToActionChange = (callToActions: DailyProgramCta[]) => {
    this.setState({
      editedVenueTemplate: {
        ...this.state.editedVenueTemplate,
        callToActions,
      },
    });
  };

  private renderPDFFileNames = () => {
    const getDownloadedFile = (params: { location: string; name: string }, index: number) => {
      return (
        <div className="inline-flex items-baseline w-full mb-2">
          <a
            className="cursor-pointer underline w-4/5 truncate"
            href={params.location}
            target="_blank"
          >
            {params.name}
          </a>
          <TextButton
            onClick={() => this.removeAttributesFile(index)}
            className="no-underline ml-3 w-1/5"
          >
            <InlineIcon name="trash" />
          </TextButton>
        </div>
      );
    };
    if (
      this.state.editedVenueTemplate.attributeFiles &&
      this.state.editedVenueTemplate.attributeFiles.length
    ) {
      return this.state.editedVenueTemplate.attributeFiles.map(getDownloadedFile);
    } else {
      return 'No attribute files added';
    }
  };

  private uploadAttributesFile = async (file: File) => {
    const { property, _id } = this.props.venueTemplate;

    const files = this.state.editedVenueTemplate.attributeFiles || [];
    const result = await uploadVenueAttributesFile(property._id, _id, file);
    files.push(result);
    this.setState({
      editedVenueTemplate: { ...this.state.editedVenueTemplate, attributeFiles: files },
    });
  };

  private removeAttributesFile = async (index: number) => {
    let copy: Array<{ location: string; name: string }> = [];
    if (this.state.editedVenueTemplate.attributeFiles) {
      copy = [...this.state.editedVenueTemplate.attributeFiles];
    }
    copy.splice(index, 1);
    this.setState({
      editedVenueTemplate: { ...this.state.editedVenueTemplate, attributeFiles: copy },
    });
  };

  private updateEditedVenue = () => {
    this.props.onEditedVenueChange();
  };

  private handleBookingHoursSubmit = async (venueHours: VenueHours[]) => {
    const venueTemplate = this.props.venueTemplate;
    const bareVenueTemplate = bare(venueTemplate);
    const venueData = {
      ...bareVenueTemplate,
      bookingHours: venueHours,
    };
    await updateVenueTemplate(venueData);

    if (this.props.refresh) this.props.refresh();
  };
}
